import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {moveItemInArray} from '@angular/cdk/drag-drop';
import {SelectionItem} from '../../selections/selection-item';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';


@Component({
    selector: 'app-column-selection-dialog',
    templateUrl: './column-selection-dialog.component.html',
    styleUrls: ['./column-selection-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatSelectionList, MatListOption, MatButton, MatIcon]
})
export class ColumnSelectionDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<ColumnSelectionDialogComponent>>(MatDialogRef);
  private data = inject(MAT_DIALOG_DATA);


  private readonly allColumnNames: SelectionItem[];
  private readonly selectedColumnNames: string[] = [];
  availableColumns!: SelectionItem[];
  selectedColumns!: SelectionItem[];

  constructor() {
    this.allColumnNames = JSON.parse(JSON.stringify(this.data.columnNames));
    for (let index: number = 0; index < this.allColumnNames.length; index++) {
      this.allColumnNames[index].id = index + 1;
    }
    this.selectedColumnNames = JSON.parse(JSON.stringify(this.data.selectedColumnNames));
  }

  ngOnInit(): void {
    if (!this.allColumnNames || this.allColumnNames.length === 0) {
      this.dialogRef.close(false);
    }
    this.selectedColumns = [];
    this.filterAvailableColumns();
    if (this.selectedColumnNames && this.selectedColumnNames.length > 0) {
      for (const selectedColumn of this.selectedColumnNames) {
        const item = this.findAvailableItem(selectedColumn);
        this.selectedColumns.push(item);
      }
      this.filterAvailableColumns();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  addSelectedColumns(columnNames: MatListOption[]) {
    for (const columnName of columnNames) {
      const availableItem = this.findAvailableItem(columnName.value);
      this.selectedColumns.push(availableItem);
    }
    this.filterAvailableColumns();
  }

  private findAvailableItem(columnName: string): SelectionItem {
    for (const item of this.availableColumns) {
      if (item.code === columnName) {
        return item;
      }
    }
    return undefined!;
  }

  private filterAvailableColumns(): void {
    const selectedNames = this.selectedColumns.map(col => col.code);
    this.availableColumns = this.allColumnNames.filter(colName => selectedNames.indexOf(colName.code) < 0);
  }

  removeSelectedColumns(columnNames: MatListOption[]) {
    for (const columnName of columnNames) {
      const selectedItem = this.findSelectedItem(columnName.value);
      const index = this.selectedColumns.indexOf(selectedItem);
      if (index >= 0) {
        this.selectedColumns.splice(index, 1);
      }
    }
    this.filterAvailableColumns();
  }

  private findSelectedItem(columnName: string): SelectionItem {
    for (const item of this.selectedColumns) {
      if (item.code === columnName) {
        return item;
      }
    }
    return undefined!;
  }

  canMoveUp(columnNames: MatListOption[]): boolean {
    if (!this.selectedColumns || this.selectedColumns.length === 0) {
      return false;
    }
    for (const columnName of columnNames) {
      const selectedItem = this.findSelectedItem(columnName.value);
      const itemIndex = this.selectedColumns.indexOf(selectedItem);
      if (itemIndex === 0) {
        return false;
      }
    }
    return true;
  }

  moveFieldsUp(columnNames: MatListOption[]): void {
    if (this.canMoveUp(columnNames)) {
      const itemList = this.buildSelectionItemList(columnNames, false);
      for (const item of itemList) {
        const currentIndex: number = item.id;
        const targetIndex: number = currentIndex - 1;
        moveItemInArray(this.selectedColumns, currentIndex, targetIndex);
      }
    }
  }

  moveFieldsDown(columnNames: MatListOption[]): void {
    if (this.canMoveDown(columnNames)) {
      const itemList = this.buildSelectionItemList(columnNames, true);
      for (const item of itemList) {
        const currentIndex: number = item.id;
        const targetIndex: number = currentIndex + 1;
        moveItemInArray(this.selectedColumns, currentIndex, targetIndex);
      }
    }
  }

  private buildSelectionItemList(columnNames: MatListOption[], sortDescending: boolean): SelectionItem[] {
    const itemList: SelectionItem[] = [];
    for (const columnName of columnNames) {
      const selectedItem = this.findSelectedItem(columnName.value);
      const selectionItem: SelectionItem = new SelectionItem();
      selectionItem.id = this.selectedColumns.indexOf(selectedItem);
      selectionItem.code = selectedItem.code;
      itemList.push(selectionItem);
    }
    if (sortDescending) {
      return itemList.sort((first, second) => second.id - first.id);
    } else {
      return itemList.sort((first, second) => first.id - second.id);
    }
  }

  canMoveDown(columnNames: MatListOption[]): boolean {
    if (!this.selectedColumns || this.selectedColumns.length === 0) {
      return false;
    }
    const maxIndex = this.selectedColumns.length - 1;
    for (const columnName of columnNames) {
      const selectedItem = this.findSelectedItem(columnName.value);
      const itemIndex = this.selectedColumns.indexOf(selectedItem);
      if (itemIndex === maxIndex) {
        return false;
      }
    }
    return true;
  }

  save(): void {
    const selectedColumnNames: string[] = [];
    for (const item of this.selectedColumns) {
      selectedColumnNames.push(item.code);
    }
    this.dialogRef.close(selectedColumnNames);
  }

}
